import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, IconButton, Paper, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SolicitudDialog from "./SolicitudDialog";
import SolicitudEditarDialog from "./SolicitudEditarDialog";
import { useAuth } from "../../../context/AuthContext";
import {
  aprobarSolicitudes, emitirSolicitudes, fmtFecha, fmtMoney, getBancosSolicitudes, getResumenSolicitudes, listarSolicitudes,
} from "../services/egresosService";

const cellSx = { py: 0.2, px: 0.5, fontSize: 11, verticalAlign: "middle" };
const inputSx = {
  font: "inherit", fontSize: 11, padding: "0 6px", border: "1px solid", borderColor: "divider", borderRadius: "4px", boxSizing: "border-box", display: "block",
  height: 26, bgcolor: "background.paper", color: "text.primary", width: 110, "&:disabled": { opacity: 0.45 },
  "&:not(:disabled)": { borderColor: "text.disabled" },
  "&:focus": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -1 },
  colorScheme: (t) => t.palette.mode, // los desplegables nativos (banco) siguen el tema de la app
};
// Color de cada vista: ámbar para lo pendiente, verde para lo aprobado. En tema oscuro se usan tonos más claros para que el texto se lea
// (los oscuros del tema claro casi no se distinguen sobre fondo oscuro); "relleno" es el color sólido de la etiqueta con cantidad (texto blanco).
const TONOS_CLARO = { 0: { principal: "#b45309", claro: "rgba(217,119,6,0.12)", relleno: "#b45309" }, 1: { principal: "#15803d", claro: "rgba(22,163,74,0.12)", relleno: "#15803d" } };
const TONOS_OSCURO = { 0: { principal: "#fbbf24", claro: "rgba(251,191,36,0.14)", relleno: "#b45309" }, 1: { principal: "#4ade80", claro: "rgba(74,222,128,0.13)", relleno: "#15803d" } };
const tonos = (t) => (t.palette.mode === "dark" ? TONOS_OSCURO : TONOS_CLARO);
const CAJAS = { chica: "Caja Chica", instalacion: "Caja Instalación", tecnico: "Caja Técnico", proyecto: "Caja Proyecto" };
const ROLES_MODIFICAR = ["admin", "gerente", "conta"];

// Solicitudes de cheque (espejo de solicita_cheque/busqueda3.php): las pendientes se aprueban
// en bloque y las aprobadas se emiten indicando N° de cheque y banco.
export default function SolicitudesTab() {
  const [vista, setVista] = useState(0); // 0 pendientes, 1 aprobadas
  const [resumen, setResumen] = useState({ pendientes: 0, aprobadas: 0 });
  const [datos, setDatos] = useState({ items: [], total_bolivianos: 0, total_dolares: 0 });
  const [bancos, setBancos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [sel, setSel] = useState({}); // id -> true
  const [emision, setEmision] = useState({}); // id -> { nocheque, banco, caja }
  const [detalleId, setDetalleId] = useState(null);
  const [editarId, setEditarId] = useState(null);
  const { user } = useAuth();
  const puedeModificar = ROLES_MODIFICAR.includes(user?.role);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const [l, r] = await Promise.all([listarSolicitudes({ estado: vista === 0 ? "pendientes" : "aprobadas" }), getResumenSolicitudes()]);
      setDatos(l.data);
      setResumen(r.data);
      setSel({});
      setEmision({});
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudieron cargar las solicitudes.");
    } finally {
      setCargando(false);
    }
  }, [vista]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { getBancosSolicitudes().then((r) => setBancos(r.data || [])).catch(() => setBancos([])); }, []);

  const marcados = useMemo(() => Object.keys(sel).filter((k) => sel[k]).map(Number), [sel]);
  const toggle = (id) => setSel((p) => ({ ...p, [id]: !p[id] }));
  const toggleTodos = (v) => setSel(Object.fromEntries(datos.items.map((i) => [i.id, v])));
  const setEm = (id, k, v) => setEmision((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));

  const aprobar = async () => {
    if (!window.confirm("¿Desea aprobar las solicitudes seleccionadas?")) return;
    setProcesando(true);
    setError("");
    setOk("");
    try {
      const { data } = await aprobarSolicitudes(marcados);
      setOk(`${data.aprobadas} solicitud(es) aprobada(s).`);
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo aprobar.");
    } finally {
      setProcesando(false);
    }
  };

  const faltanDatos = marcados.some((id) => !(emision[id]?.nocheque || "").trim() || !emision[id]?.banco);

  const emitir = async () => {
    if (!window.confirm("¿Desea emitir los cheques seleccionados?")) return;
    setProcesando(true);
    setError("");
    setOk("");
    try {
      const items = marcados.map((id) => ({ id, nocheque: emision[id].nocheque.trim(), banco: emision[id].banco, caja: emision[id].caja || "" }));
      const { data } = await emitirSolicitudes(items);
      setOk(`Cheques emitidos: ${data.emitidos.map((e) => `solicitud ${e.solicitud} → egreso ${e.egreso}`).join(", ")}.`);
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo emitir.");
    } finally {
      setProcesando(false);
    }
  };

  const esPend = vista === 0;
  const columnas = ["", "ID", "Fecha", "Dirigido a", "Concepto", "N° contrato", "N° referencia", "N° factura", "Servicio", "Monto Bs", "Monto USD", "Mes servicio",
    "Estado", "Depositado a", "Proyecto", "Solicitante", ...(esPend ? [] : ["N° cheque", "Banco", "Caja"]), ""];
  const colorFila = (r) => (r.estado === "autorizado" ? "success.light" : r.con_orden_compra ? "warning.light" : "info.light");

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 1 }}>
        <Tabs value={vista} onChange={(_, v) => setVista(v)} slotProps={{ indicator: { sx: { height: 3, bgcolor: (t) => tonos(t)[vista].principal } } }}
          sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, py: 0.25, fontSize: 12.5, textTransform: "none", fontWeight: 700, gap: 0.75 } }}>
          {[["Pendientes", resumen.pendientes], ["Solicitudes aprobadas", resumen.aprobadas]].map(([etq, cantidad], i) => (
            <Tab key={etq} sx={{ color: (t) => tonos(t)[i].principal, opacity: vista === i ? 1 : 0.75, "&.Mui-selected": { color: (t) => tonos(t)[i].principal, bgcolor: (t) => tonos(t)[i].claro } }}
              label={(
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {etq}
                  <Chip size="small" label={cantidad} sx={{ height: 18, minWidth: 22, fontSize: 11, fontWeight: 800, borderRadius: 0, color: cantidad ? "#fff" : "text.secondary", bgcolor: (t) => (cantidad ? tonos(t)[i].relleno : t.palette.action.selected) }} />
                </Box>
              )} />
          ))}
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 1, py: 0 }} onClose={() => setOk("")}>{ok}</Alert>}

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, bgcolor: (t) => tonos(t)[vista].claro, borderBottom: 2, borderColor: (t) => tonos(t)[vista].principal }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: (t) => tonos(t)[vista].principal }}>{esPend ? "Solicitudes pendientes de aprobación" : "Solicitudes aprobadas (por emitir)"} ({datos.items.length})</Typography>
          {cargando && <CircularProgress size={14} />}
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={cargar} disabled={cargando} sx={{ minHeight: 24, height: 24, fontSize: 11, color: (t) => tonos(t)[vista].principal }}>Actualizar</Button>
        </Box>
        <TableContainer sx={{ maxHeight: "62vh" }}>
          <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx, "& .MuiTableCell-stickyHeader": { bgcolor: "background.paper", borderBottom: 2, borderColor: "divider", color: "text.secondary", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.3 } }}>
            <TableHead>
              <TableRow>
                {columnas.map((h, i) => (
                  <TableCell key={`${h}-${i}`} sx={{ fontWeight: 800, whiteSpace: "nowrap" }} padding={i === 0 ? "checkbox" : "normal"}>
                    {i === 0 ? <Checkbox size="small" sx={{ p: 0.3 }} checked={datos.items.length > 0 && marcados.length === datos.items.length}
                      indeterminate={marcados.length > 0 && marcados.length < datos.items.length} onChange={(e) => toggleTodos(e.target.checked)} /> : h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!datos.items.length ? (
                <TableRow><TableCell colSpan={columnas.length}><Typography variant="body2" color="text.secondary">No existen solicitudes.</Typography></TableCell></TableRow>
              ) : datos.items.map((r) => {
                const activo = !!sel[r.id];
                const em = emision[r.id] || {};
                return (
                  <TableRow key={r.id} hover selected={activo} sx={{ "& > td:nth-of-type(2)": { borderLeft: 4, borderLeftColor: colorFila(r), borderLeftStyle: "solid" } }}>
                    <TableCell padding="checkbox"><Checkbox size="small" sx={{ p: 0.3 }} checked={activo} onChange={() => toggle(r.id)} /></TableCell>
                    <TableCell>{r.id}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(r.fecha)}</TableCell>
                    <TableCell>{r.nombre}</TableCell>
                    <TableCell>{r.concepto}</TableCell>
                    <TableCell>{r.nro_contrato}</TableCell>
                    <TableCell>{r.nro_referencia}</TableCell>
                    <TableCell>{r.facturas.join(", ")}</TableCell>
                    <TableCell>{r.servicio}</TableCell>
                    <TableCell align="right">{fmtMoney(r.bolivianos)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.dolares)}</TableCell>
                    <TableCell>{r.mes}</TableCell>
                    <TableCell>{r.estado}</TableCell>
                    <TableCell>{r.deposito}</TableCell>
                    <TableCell>{r.proyecto ? <><b>{r.proyecto.nombre}</b>: {r.proyecto.descripcion}</> : ""}</TableCell>
                    <TableCell>{r.usu}</TableCell>
                    {!esPend && (
                      <>
                        <TableCell><Box component="input" disabled={!activo} value={em.nocheque || ""} onChange={(e) => setEm(r.id, "nocheque", e.target.value)} sx={inputSx} /></TableCell>
                        <TableCell>
                          <Box component="select" disabled={!activo} value={em.banco || ""} onChange={(e) => setEm(r.id, "banco", e.target.value)} sx={{ ...inputSx, width: 110 }}>
                            <option value="" />
                            {bancos.map((b) => <option key={b} value={b}>{b}</option>)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.caja_sugerida && (
                            <label>
                              <Checkbox size="small" sx={{ p: 0.3 }} disabled={!activo} checked={em.caja === r.caja_sugerida} onChange={(e) => setEm(r.id, "caja", e.target.checked ? r.caja_sugerida : "")} />
                              Cargar en {CAJAS[r.caja_sugerida]}
                            </label>
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {r.total_archivos > 0 && (
                        <Tooltip title={`${r.total_archivos} archivo(s) adjunto(s): clic para verlos`}>
                          <IconButton size="small" onClick={() => setDetalleId(r.id)} sx={{ p: 0.3, mr: 0.25, gap: 0.2, fontSize: 11, fontWeight: 700, color: "text.secondary" }} aria-label={`Adjuntos de la solicitud ${r.id}`}>
                            <AttachFileIcon sx={{ fontSize: 16 }} />{r.total_archivos}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Ver solicitud">
                        <IconButton size="small" color="primary" onClick={() => setDetalleId(r.id)} sx={{ p: 0.3 }}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Tooltip>
                      {puedeModificar && (
                        <Tooltip title="Modificar solicitud">
                          <IconButton size="small" onClick={() => setEditarId(r.id)} sx={{ p: 0.3, ml: 0.25 }} aria-label={`Modificar la solicitud ${r.id}`}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {datos.items.length > 0 && (
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell colSpan={9} align="right" sx={{ fontWeight: 800 }}>Totales</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: (t) => (t.palette.mode === "dark" ? t.palette.error.light : t.palette.error.main) }}>{fmtMoney(datos.total_bolivianos)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: (t) => (t.palette.mode === "dark" ? t.palette.error.light : t.palette.error.main) }}>{fmtMoney(datos.total_dolares)}</TableCell>
                  <TableCell colSpan={columnas.length - 11} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, p: 1, borderTop: 1, borderColor: "divider" }}>
          {!esPend && marcados.length > 0 && faltanDatos && <Typography sx={{ fontSize: 11 }} color="text.secondary">Completa N° de cheque y banco de cada solicitud marcada.</Typography>}
          {esPend ? (
            <Button size="small" variant="contained" disabled={!marcados.length || procesando} onClick={aprobar}>Aprobar</Button>
          ) : (
            <Button size="small" variant="contained" disabled={!marcados.length || faltanDatos || procesando} onClick={emitir}>Emitir cheque</Button>
          )}
        </Box>
      </Paper>

      <SolicitudDialog id={detalleId} onClose={() => setDetalleId(null)} onCambio={cargar}
        onModificar={puedeModificar ? (sid) => { setDetalleId(null); setEditarId(sid); } : undefined} />
      <SolicitudEditarDialog id={editarId} onClose={() => setEditarId(null)} onGuardada={(msg) => { setOk(msg); cargar(); }} />
    </Box>
  );
}
